/**
 * Un exercice qui ne se tape pas : il se dessine, puis se photographie.
 *
 * `exercise` §11 : une construction géométrique, un schéma, un montage. Le
 * résultat est une figure, et la figure est la réponse — aucun correcteur
 * automatique ne la lit, et c'est un parent qui coche une grille de contrôle
 * formulée pour être vérifiable sans connaître la matière.
 *
 * **Cet écran n'affiche jamais de verdict**, et c'est la différence de fond
 * avec `ExercisePanel`. L'enfant envoie et sort ; ce qu'il obtient en retour est
 * la certitude que le travail est parti, pas une note. Le verdict viendra du
 * clic d'un parent, plus tard, et le lui promettre ici — même sous la forme
 * d'un « en attente… » qui tourne — ferait attendre devant un écran quelque
 * chose qui peut prendre la soirée.
 *
 * La photo va **directement au stockage** par URL présignée (`uploadPhoto`), et
 * seule sa clé traverse l'API. C'est l'invariant 7, et c'est aussi ce qui
 * permet à un envoi de 12 Mo de ne pas occuper un worker.
 */

import { useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { ExerciseOut } from "@/api/schema";
import { uploadPhoto } from "@/photo/upload";
import { EnonceImages, imagesDeLEnonce } from "@/ui/EnonceImage";
import { EtiquetteParascolaire } from "@/ui/Parascolaire";
import { TexteMathematique } from "@/ui/TexteMathematique";
import { Button, Card, ErrorNote } from "@/ui/primitives";

type Etat = "a-faire" | "envoi" | "envoye" | "sur-papier" | "panne" | "deja-rendu";

/** Ce que l'enfant lit à chaque étape. Séparé pour être testable sans DOM. */
export function consigne(etat: Etat): string {
  switch (etat) {
    case "envoi":
      return "Envoi en cours…";
    case "envoye":
      return "C'est envoyé. Un de tes parents va regarder ton travail.";
    case "sur-papier":
      return "C'est enregistré. Montre ton cahier à un de tes parents : il le relira.";
    case "panne":
      return "Ça n'est pas parti. Réessaie dans un instant.";
    case "deja-rendu":
      return "Tu as déjà rendu cet exercice : pas besoin de l'envoyer une seconde fois.";
    default:
      return "Fais l'exercice sur ton cahier, puis photographie-le.";
  }
}

/**
 * Un refus du serveur n'est pas une panne. `deja_rendu` (une réponse a déjà été
 * saisie) et `rendu_sur_papier` (une demande attend déjà) disent la même chose
 * à l'enfant : c'est rendu. « Réessaie » l'enverrait buter une seconde fois.
 */
export function etatDEchec(erreur: unknown): Etat {
  if (
    erreur instanceof ApiError &&
    erreur.status === 409 &&
    ["deja_rendu", "rendu_sur_papier", "deja_valide"].includes(erreur.code)
  ) {
    return "deja-rendu";
  }
  return "panne";
}

export function RenduAuParent({
  item,
  retour,
  suivant,
  libelleRetour = "← Revenir à la liste",
  libelleSuivant = "Un autre",
  revenirALaSaisie,
}: {
  item: ExerciseOut;
  retour: () => void;
  suivant: () => void;
  libelleRetour?: string;
  libelleSuivant?: string;
  /**
   * Présent quand l'exercice se tape aussi (un sujet à images) : l'enfant qui
   * a choisi le cahier peut revenir à la saisie tant que rien n'est parti.
   */
  revenirALaSaisie?: () => void;
}) {
  const [etat, setEtat] = useState<Etat>("a-faire");
  const images = imagesDeLEnonce(item.statement_image_urls, item.statement_image_url);

  // Un exercice marqué pour le parent (énoncé mal décodé, corrigé du
  // professeur…) : son texte n'est pas montré, l'énoncé est sur l'image.
  const marque = Boolean(item.motif_parent);

  const envoyer = async (file: File) => {
    setEtat("envoi");
    try {
      const photo = await uploadPhoto(file);
      await call("demanderUneValidation", {
        params: { exercise_id: item.exercise_id },
        body: { photo_key: photo.key },
      });
      setEtat("envoye");
    } catch (erreur) {
      setEtat(etatDEchec(erreur));
    }
  };

  // « C'est fait sur mon cahier » : la demande part sans photo, et le parent
  // relit le cahier lui-même. Aucun verdict n'est annoncé ici non plus.
  const surPapier = async () => {
    setEtat("envoi");
    try {
      await call("demanderUneValidation", {
        params: { exercise_id: item.exercise_id },
        body: { photo_key: null },
      });
      setEtat("sur-papier");
    } catch (erreur) {
      setEtat(etatDEchec(erreur));
    }
  };
  const termine = etat === "envoye" || etat === "sur-papier" || etat === "deja-rendu";

  return (
    <>
      <div className="revision__retour">
        <Button variant="quiet" onClick={retour}>
          {libelleRetour}
        </Button>
      </div>
      <Card title="À faire sur ton cahier">
        {images.length ? (
          <>
            <EtiquetteParascolaire nom={item.parascolaire} />
            <EnonceImages images={images} statement={item.statement} />
          </>
        ) : marque ? (
          <p className="muted">{item.motif_parent_phrase ?? item.statement}</p>
        ) : (
          <TexteMathematique>{item.statement}</TexteMathematique>
        )}
      </Card>
      <Card title="Photographier">
        <p className="muted">{consigne(etat)}</p>
        {!termine ? (
          <input
            className="input"
            type="file"
            accept="image/*"
            capture="environment"
            disabled={etat === "envoi"}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void envoyer(file);
            }}
          />
        ) : null}
        {!termine ? (
          <div className="revision__suite">
            <Button variant="quiet" disabled={etat === "envoi"} onClick={() => void surPapier()}>
              C'est fait sur mon cahier
            </Button>
            {revenirALaSaisie ? (
              <Button variant="quiet" disabled={etat === "envoi"} onClick={revenirALaSaisie}>
                Je préfère taper ma réponse
              </Button>
            ) : null}
          </div>
        ) : null}
        {etat === "panne" ? <ErrorNote>{consigne("panne")}</ErrorNote> : null}
        <p className="muted small">
          La photo part directement au stockage : elle ne passe pas par l'application, et
          personne d'autre que tes parents ne la verra.
        </p>
        {termine ? (
          <div className="revision__suite">
            <Button onClick={suivant}>{libelleSuivant}</Button>
            <Button variant="quiet" onClick={retour}>
              Terminer
            </Button>
          </div>
        ) : null}
      </Card>
    </>
  );
}
